import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { InputWithLabel } from 'src/design-system/InputWithLabel';
import { Select } from 'src/design-system/Select';
import { Client } from 'src/admin/users/types';
import { useForm, zodResolver } from '@mantine/form';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { z } from 'zod';
import { GenericErrorModal } from '../error/GenericErrorModal';
import PageLoader from '../UI/loader/PageLoader';
import { useClientsContext } from './context/ClientsContext';
import { useAdvisors } from './hooks/useAdvisors';
import { useClients } from './hooks/useClients';
import { useEffect } from 'react';
import ModalBackButton from 'src/UI/common/ModalBackButton';
import { Mixpanel } from 'src/tracking/mixpanel/Mixpanel';
import { MixpanelEvents } from 'src/tracking/mixpanel/MixpanelEvents';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { formatName } from 'src/utils/nameFormatting';

type Props = {
  opened: boolean;
  close: () => void;
  canGoBack?: boolean;
  previousStep?: () => void;
  emailAddress?: string;
  prefill?: { firstName?: string; lastName?: string; emailAddress?: string };
  navigateAfterCreation?: boolean;
  onClientCreated?: (client: Client) => void;
};

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  emailAddress: z.string().email('Please enter a valid email address').nullable().optional(),
  advisorUserId: z.string().min(1, 'Please select an advisor'),
});

export default function CreateClientModalV2({
  opened,
  close,
  canGoBack,
  previousStep,
  emailAddress,
  prefill,
  navigateAfterCreation = true,
  onClientCreated,
}: Props) {
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { advisors, isLoading: isAdvisorsLoading, refetch: refetchAdvisors } = useAdvisors();
  const { createClient, error, isLoading } = useClientsContext();
  const { clients } = useClients();
  const isDuplicateEmail = (emailAddress: string) => {
    return clients.some(
      client => client.emailAddress?.toLowerCase() === emailAddress.toLowerCase(),
    );
  };

  useEffect(() => {
    if (opened) {
      refetchAdvisors();
    }
  }, [opened, refetchAdvisors]);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      firstName: '',
      lastName: '',
      emailAddress: emailAddress || '',
      advised: true,
      advisorUserId: '',
    },
    validate: values => {
      const zodErrors = zodResolver(formSchema)(values);
      if (!zodErrors.emailAddress && isDuplicateEmail(values.emailAddress || '')) {
        zodErrors.emailAddress = 'This email address is already in use.';
      }

      return zodErrors;
    },
    validateInputOnBlur: true,
  });

  useEffect(() => {
    if (opened && prefill) {
      if (prefill.firstName) form.setFieldValue('firstName', prefill.firstName);
      if (prefill.lastName) form.setFieldValue('lastName', prefill.lastName);
      if (prefill.emailAddress) form.setFieldValue('emailAddress', prefill.emailAddress);
    }
  }, [opened, prefill]);

  useEffect(() => {
    if (opened && currentUser?.id && !form.values.advisorUserId) {
      form.setFieldValue('advisorUserId', currentUser.id);
    }
  }, [opened, currentUser?.id]);

  const onCreateClient = async (values: {
    emailAddress: string | null | undefined;
    firstName: string;
    lastName: string;
    advisorUserId: string;
    advised: boolean;
  }) => {
    try {
      const client = await createClient(values, navigateAfterCreation);
      Mixpanel.track(MixpanelEvents.CLIENT_CREATED);
      dispatchAppEvent('CLIENT_CREATED');
      close();
      form.reset();
      if (client) onClientCreated?.(client);
    } catch (e) {
      if (ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, e)) {
        form.setFieldError('emailAddress', e.message);
      }
      notifyAndLogError(
        'Failed to create client',
        'An error occurred while creating the client. Please try again.',
        e,
        { clientData: values },
      );
    }
  };

  if (error instanceof ApiError && !ApiError.isApiError(ApiErrorCode.DUPLICATE_EMAIL, error)) {
    return <GenericErrorModal opened={true} onClose={close} />;
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      radius="lg"
      size="md"
      title={
        <Text size="displaySm" fw={FONT_WEIGHTS.semibold} c="black">
          Individual details
        </Text>
      }
    >
      {previousStep && (
        <ModalBackButton previousStep={previousStep} canGoBack={Boolean(canGoBack)} />
      )}
      {isLoading || isAdvisorsLoading ? (
        <PageLoader absolute={false} />
      ) : (
        <form onSubmit={form.onSubmit(values => onCreateClient(values))}>
          <Stack gap="sm">
            <Group gap="xs" align="flex-start" grow>
              <InputWithLabel
                label="First name"
                placeholder="Jane"
                {...form.getInputProps('firstName')}
              />
              <InputWithLabel
                label="Last name"
                placeholder="Doe"
                {...form.getInputProps('lastName')}
              />
            </Group>
            <InputWithLabel
              label="Email"
              placeholder="Client email address"
              {...form.getInputProps('emailAddress')}
            />
            <Select
              label="Is this person a client?"
              placeholder="Please select"
              data={[
                { value: 'true', label: 'Yes, they are an advised client' },
                { value: 'false', label: 'No, they are a contact' },
              ]}
              onChange={value => form.setFieldValue('advised', value === 'true')}
              value={form.values.advised?.toString()}
            />
            {(isSystemAdmin || isServiceAdmin) && (
              <Select
                label="Advisor"
                placeholder="Select advisor"
                data={advisors.map(advisor => ({
                  value: advisor.id,
                  label: formatName(advisor.firstName, advisor.lastName),
                }))}
                {...form.getInputProps('advisorUserId')}
              />
            )}
            <Button
              type="submit"
              disabled={!form.isValid()}
              variant="filled"
              color="brand"
              c="white"
              mt="sm"
              mx="auto"
              px={132}
              py="xs"
              h="auto"
              fz="md"
              fw={FONT_WEIGHTS.semibold}
              style={{ display: 'block', borderRadius: 20 }}
            >
              Add individual
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
